// Local-dev account tools: the dev account's Free/Pro switch and its credit
// refill, plus the signal that opens the refill prompt when a solve fails.
//
// Two locks, and every dev surface needs both:
//   1. DEV_TOOLS_BUILD — build time. Next inlines NODE_ENV, so in a production
//      bundle this is the literal `false` and everything behind it is dead code.
//   2. `dev_tools` on /api/auth/me — run time. The backend (and the mock
//      backend) sets it only for dev@local under the local-dev bypass, and its
//      /api/auth/dev/* routes 404 for anyone else.

export const DEV_TOOLS_BUILD = process.env.NODE_ENV === 'development';

export type DevPlan = 'free' | 'pro';

export function parseDevPlan(value: unknown): DevPlan | null {
  return value === 'free' || value === 'pro' ? value : null;
}

async function postDev(path: string, body?: unknown): Promise<void> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    ...(body === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
  if (!res.ok) throw new Error(`${path} failed (${res.status})`);
}

export function setDevPlan(plan: DevPlan): Promise<void> {
  return postDev('/api/auth/dev/plan', { plan });
}

export function resetDevCredits(): Promise<void> {
  return postDev('/api/auth/dev/reset-credits');
}

// ── Compute-failure signal ─────────────────────────────────────────────────

export const DEV_COMPUTE_FAILED_EVENT = 'rt-dev-compute-failed';

export interface DevComputeFailure {
  /** `exhausted` = a submit was refused for credits; `failed` = a job errored. */
  reason: 'exhausted' | 'failed';
  message: string;
}

const SUBMIT_PATHS = ['/api/admin/jobs', '/api/onboarding/placement'];
const POLL_PATH = /^\/api\/admin\/jobs\/(?!active$)[^/]+$/;

/**
 * Classify one job-API response. Pure, so it is tested without a network:
 * a 402 from either submit door is an exhausted pool; a poll that reports
 * `status: "error"` is a failed job — except a user cancel, which is not a
 * failure. Everything else (successes, the freebie door's 409 fallback, the
 * active-jobs list) is not a failure.
 */
export function classifyJobResponse(
  pathname: string,
  method: string,
  status: number,
  body: unknown,
): DevComputeFailure | null {
  const upper = method.toUpperCase();
  if (upper === 'POST' && SUBMIT_PATHS.includes(pathname) && status === 402) {
    return { reason: 'exhausted', message: 'Out of free compute credits.' };
  }
  if (upper === 'GET' && POLL_PATH.test(pathname) && body && typeof body === 'object') {
    const poll = body as { status?: unknown; error?: unknown; error_code?: unknown };
    if (poll.status !== 'error' || poll.error_code === 'cancelled') return null;
    return {
      reason: 'failed',
      message: typeof poll.error === 'string' && poll.error ? poll.error : 'The solve failed.',
    };
  }
  return null;
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Wrap the admin API's fetch so job failures announce themselves as a window
 * event. Reads a clone and never awaits it, so the caller sees the untouched
 * response with no added latency.
 */
export function watchJobFailures(inner: FetchLike): FetchLike {
  return async (input, init) => {
    const res = await inner(input, init);
    const pathname = new URL(input, window.location.origin).pathname;
    const method = init?.method ?? 'GET';
    if (method.toUpperCase() === 'GET' ? POLL_PATH.test(pathname) : SUBMIT_PATHS.includes(pathname)) {
      void res
        .clone()
        .json()
        .catch(() => null)
        .then((body: unknown) => {
          const failure = classifyJobResponse(pathname, method, res.status, body);
          if (failure) {
            window.dispatchEvent(
              new CustomEvent<DevComputeFailure>(DEV_COMPUTE_FAILED_EVENT, { detail: failure }),
            );
          }
        });
    }
    return res;
  };
}
